import React, { useState } from 'react'
import { Button, Dialog, DialogActions, DialogContent, MenuItem, Typography } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import type { FormikProps } from 'formik'
import { Field } from 'formik'
import * as Yup from 'yup'
import { DateTime } from 'luxon'
import { FormDateTimePicker, CustomTextField, GraphQLForm } from 'src/form'
import { ClosableDialogTitle } from 'src/legacy/components/mui'
import { UpdateQueryOptions } from '@apollo/client'
import { ScheduledVideoCallReason } from 'src/graphql.generated'
import ParticipantTime from 'src/date/ParticipantTime'
import { trackCreateCheckinCallAppointment } from 'src/mixpanel/api'

import { GetUncancelledInProgramCallsDocument } from 'src/participantHistoryForm/form/checkinCall/getUncancelledInProgramCalls.generated'
import {
  useScheduleVideoCallMutation,
  GetParticipantVideoCallBookingInformationDocument,
  useCallDurationInMinutesQuery,
} from '../videoCallBookingHeaderQueries.generated'

export const VIDEO_CALL_REASON_LABELS: Record<ScheduledVideoCallReason, string> = {
  ENGAGEMENT_ISSUE: 'Program engagement',
  ESCALATING_SYMPTOMS: 'Escalating symptoms',
  LIFE_ISSUE: 'Life event(s)',
  OTHER_REASON: 'Other',
  PATIENT_INITIATED: 'Patient initiated',
}

const useStyles = makeStyles((theme) => ({
  instructionText: {
    paddingTop: theme.spacing(2),
  },
}))

enum SchedulingView {
  SCHEDULE_TIME = 'SCHEDULE_TIME',
  CONFIRM_TIME = 'CONFIRM_TIME',
}

type ScheduleVideoCallForm = {
  start: number
  uid: string
  reason: ScheduledVideoCallReason
}

const ScheduleVideoCallSchema = (uid: string) =>
  Yup.object({
    start: Yup.number().required(),
    uid: Yup.string().required().default(uid),
    reason: Yup.string().oneOf(Object.values(ScheduledVideoCallReason)).required(),
  }).required()

export type ScheduleInProgramVideoCallDialogProps = {
  uid: string
  firstname?: string | null
  lastname?: string | null
  open: boolean
  onClose: () => void
}

const VIDEO_CALL_REASONS_INPUT = Object.entries(VIDEO_CALL_REASON_LABELS).filter(
  ([reason]) => reason !== ScheduledVideoCallReason.PatientInitiated
)

const ScheduleInProgramVideoCallDialog = ({
  uid,
  firstname,
  lastname,
  onClose,
  open,
}: ScheduleInProgramVideoCallDialogProps) => {
  const [schedulingScreen, setSchedulingScreen] = useState(SchedulingView.SCHEDULE_TIME)

  const [scheduleVideoCall] = useScheduleVideoCallMutation({
    refetchQueries: [GetParticipantVideoCallBookingInformationDocument, GetUncancelledInProgramCallsDocument],
  })

  const { data: callDurationData, loading: callDurationLoading } = useCallDurationInMinutesQuery({
    variables: { uid },
    skip: !open, // Only run query when dialog is open
  })

  const classes = useStyles()

  const validation = ScheduleVideoCallSchema(uid)

  const handleSubmit = async ({ variables }: UpdateQueryOptions<ScheduleVideoCallForm>) => {
    const { start, ...queryVariables } = variables!
    const startTime = new Date(start)

    trackCreateCheckinCallAppointment(queryVariables.reason)
    await scheduleVideoCall({ variables: { ...queryVariables, startTime } })
  }

  const close = () => {
    onClose()
    setSchedulingScreen(SchedulingView.SCHEDULE_TIME)
  }

  return (
    <Dialog onClose={onClose} open={open}>
      <GraphQLForm
        initialErrors={{ reason: 'This is required' }} // Telling formik that the form is not initially valid
        onSubmit={handleSubmit}
        onSuccess={close}
        validationSchema={validation}
        render={({ values, isSubmitting, isValid }: FormikProps<ScheduleVideoCallForm>) => {
          const timezoneAbbr = DateTime.now().toFormat('ZZZZ')
          const startTimeWithoutEndTime = values.start
            ? `${DateTime.fromMillis(values.start).toFormat('hh:mm a')} ${timezoneAbbr}`
            : ''
          const startTimeWithEndTime = values.start
            ? `${DateTime.fromMillis(values.start).toFormat('hh:mm')}-${DateTime.fromMillis(values.start)
                .plus({ minutes: callDurationData?.callDurationInMinutes })
                .toFormat('hh:mm a')} ${timezoneAbbr}`
            : ''
          return (
            <>
              <ClosableDialogTitle onClose={close}>Create a check-in call appointment</ClosableDialogTitle>

              <DialogContent hidden={schedulingScreen !== SchedulingView.SCHEDULE_TIME}>
                <Field
                  component={FormDateTimePicker}
                  fullWidth
                  helperText={<ParticipantTime uid={uid} time={values.start} />}
                  label="Select Date and Time"
                  name="start"
                />

                <Typography classes={{ root: classes.instructionText }} variant="body1">
                  Please select from the options below to describe the nature of this call.
                </Typography>

                <Field component={CustomTextField} label="Reason for call" name="reason" type="select">
                  {VIDEO_CALL_REASONS_INPUT.map(([reason, reasonText]) => (
                    <MenuItem key={reason} value={reason}>
                      {reasonText}
                    </MenuItem>
                  ))}
                </Field>
              </DialogContent>

              <DialogContent hidden={schedulingScreen !== SchedulingView.CONFIRM_TIME}>
                <Typography variant="body1">
                  You are about to create a check-in call appointment with{' '}
                  {firstname && lastname ? (
                    <strong>
                      {firstname} {lastname}
                    </strong>
                  ) : (
                    'the participant'
                  )}{' '}
                  for {values.start ? DateTime.fromMillis(values.start).toFormat('MMMM dd, yyyy') : ''} at{' '}
                  <strong>
                    {callDurationLoading || !callDurationData?.callDurationInMinutes
                      ? startTimeWithoutEndTime
                      : startTimeWithEndTime}
                  </strong>
                  .
                </Typography>
              </DialogContent>

              <DialogActions>
                {schedulingScreen === SchedulingView.SCHEDULE_TIME && (
                  <>
                    <Button color="inherit" onClick={close}>
                      Cancel
                    </Button>

                    <Button
                      color="primary"
                      disabled={!isValid}
                      onClick={() => setSchedulingScreen(SchedulingView.CONFIRM_TIME)}
                    >
                      Next
                    </Button>
                  </>
                )}

                {schedulingScreen === SchedulingView.CONFIRM_TIME && (
                  <>
                    <Button color="inherit" onClick={() => setSchedulingScreen(SchedulingView.SCHEDULE_TIME)}>
                      Back
                    </Button>

                    <Button color="primary" disabled={isSubmitting} type="submit">
                      Confirm
                    </Button>
                  </>
                )}
              </DialogActions>
            </>
          )
        }}
      />
    </Dialog>
  )
}

export default ScheduleInProgramVideoCallDialog
